import { BlockStack, Checkbox, LegacyStack, Text } from '@shopify/polaris'
import React, { useCallback } from 'react'

import { MemoedTextField } from '../common/helpers'
import { HelpLabel, LegacyHelpLabel } from '../common/HelpIcon'
import { useShopCurrency } from '../common/Price'
import {
  normalizeWorkflowActionPrice,
  WorkflowAction,
  workflowActionLabels,
  WorkflowEditDeliveryMethodDataType,
} from '../common/workflow-models'
import { WorkflowActionPriceFields } from './WorkflowActionPriceFields'

export const EditDeliveryMethodAction = ({
  onChange,
  index,
  params,
  currentTitleError,
  currentCodeError,
  currentPriceError,
}: {
  onChange: (action: WorkflowAction, index: number) => void
  index: number
  params: WorkflowEditDeliveryMethodDataType
  currentTitleError: string | null
  currentCodeError: string | null
  currentPriceError: string | null
}) => {
  const shopCurrency = useShopCurrency()

  const [deliveryMethodTitle, deliveryMethodCode] = params
  const deliveryPrice = params[2] ?? null

  const perCurrencyPrices =
    deliveryPrice === null ? null : normalizeWorkflowActionPrice(deliveryPrice, shopCurrency)

  const handleUpdate = useCallback(
    (updatedParams: WorkflowEditDeliveryMethodDataType) => {
      onChange(['edit-delivery-method', updatedParams], index)
    },
    [index, onChange]
  )

  return (
    <LegacyStack vertical>
      <Text variant="headingSm" as="h2">
        {workflowActionLabels['edit-delivery-method']}
      </Text>

      <MemoedTextField
        label={
          <Checkbox
            checked={deliveryMethodTitle !== null}
            onChange={(checked) =>
              handleUpdate([checked ? '' : null, deliveryMethodCode, deliveryPrice])
            }
            label={
              <LegacyHelpLabel title="Change delivery method title">
                Enable to change the delivery method label displayed to customers on the order and
                subscription details pages
              </LegacyHelpLabel>
            }
          />
        }
        autoComplete="off"
        value={deliveryMethodTitle ?? ''}
        onChange={(val: string) => handleUpdate([val, deliveryMethodCode, deliveryPrice])}
        error={currentTitleError ?? undefined}
        disabled={deliveryMethodTitle === null}
      />
      <MemoedTextField
        label={
          <Checkbox
            checked={deliveryMethodCode !== null}
            onChange={(checked) =>
              handleUpdate([deliveryMethodTitle, checked ? '' : null, deliveryPrice])
            }
            label={
              <LegacyHelpLabel title="Change delivery method code">
                Enable to change the delivery method code which is an arbitrary identifier that may
                be used for integration with 3rd party apps
              </LegacyHelpLabel>
            }
          />
        }
        autoComplete="off"
        value={deliveryMethodCode ?? ''}
        onChange={(val: string) => handleUpdate([deliveryMethodTitle, val, deliveryPrice])}
        error={currentCodeError ?? undefined}
        disabled={deliveryMethodCode === null}
      />
      <BlockStack gap="200">
        <Checkbox
          checked={perCurrencyPrices !== null}
          onChange={(checked) =>
            handleUpdate([
              deliveryMethodTitle,
              deliveryMethodCode,
              checked ? { [shopCurrency]: '0.00' } : null,
            ])
          }
          label={
            <HelpLabel tooltip='Enable to change delivery price. Set the price per currency. Subscriptions are billed in their own currency, so this action applies only when a price for the subscription&apos;s currency is listed. Choose "Any currency" to use one price for every currency that is not listed.'>
              Change delivery price
            </HelpLabel>
          }
        />
        {perCurrencyPrices !== null && (
          <WorkflowActionPriceFields
            prices={perCurrencyPrices}
            onChange={(prices) => handleUpdate([deliveryMethodTitle, deliveryMethodCode, prices])}
            error={currentPriceError}
            index={index}
            bannerText="The delivery method will only be edited for subscriptions billed in the currencies listed here."
          />
        )}
      </BlockStack>
    </LegacyStack>
  )
}
